'use client';

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  Row,
  RowData,
  Updater,
  useReactTable,
} from '@tanstack/react-table';

// Lets columns opt out of the default `max-w-sm truncate whitespace-nowrap`
// cell styling — e.g. ID columns where the full value must remain visible.
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    cellClassName?: string;
  }
}

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { cn } from 'lib/utils';
import { DataTablePagination, DataTablePaginationState } from './data-table-pagination';
import { DataTableToolbar, FacetOptions, Facets } from './data-table-toolbar';
import { useMemo, useState } from 'react';
import { Skeleton } from './skeleton';
import { DataTableRowActions, RowAction } from './data-table-row-actions';
import { DataTableFacetedFilterInitialValue } from './data-table-faceted-filter';

interface DataTableProps<TData, TValue> {
  columns: (ColumnDef<TData, TValue> & { accessorKey?: string })[];
  data: TData[];
  pageSize?: number;
  onRowClicked?: (row: TData) => void;
  pageIndex?: number;
  pageCount?: number;
  onPaginationChange?: (data: DataTablePaginationState) => void;
  onColumnFiltersChange?: (data: ColumnFiltersState) => void;
  onRowActionClick?: (data: Row<TData>) => void;
  hiddenFacets?: string[];
  facetOptions?: FacetOptions;
  loading?: boolean;
  emptyState?: React.ReactNode;
  hideToolbar?: boolean;
  toolbarItems?: React.ReactNode;
  manualPagination?: boolean;
  showSkipToFirstPageButton?: boolean;
  showSkipToLastPageButton?: boolean;
  hideTotalPageCount?: boolean;
  rowActions?: RowAction[];
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  initialSelectedFacetValues?: DataTableFacetedFilterInitialValue[];
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClicked,
  pageSize,
  pageIndex = 0,
  pageCount = 1,
  hiddenFacets,
  facetOptions,
  initialSelectedFacetValues,
  loading,
  emptyState,
  onPaginationChange,
  onColumnFiltersChange,
  hideToolbar,
  toolbarItems,
  manualPagination = false,
  showSkipToFirstPageButton = false,
  showSkipToLastPageButton = false,
  hideTotalPageCount = false,
  rowActions,
  onRowActionClick,
  hasNextPage,
  hasPreviousPage,
  onSearch,
  searchPlaceholder,
}: DataTableProps<TData, TValue>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const manualPaginationSettings = manualPagination
    ? {
        pageCount,
        manualPagination: true,
        state: {
          pagination: {
            pageIndex,
            pageSize: pageSize ?? 10,
          },
          columnFilters,
        },
        onPaginationChange: (updater: Updater<DataTablePaginationState>) => {
          const newPagination =
            typeof updater === 'function'
              ? updater({
                  pageIndex,
                  pageSize: pageSize ?? 10,
                })
              : updater;

          onPaginationChange?.(newPagination);
        },
      }
    : {};

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...manualPaginationSettings,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnFiltersChange: (updater) => {
      const filters = typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(filters);
      onColumnFiltersChange?.(filters);
    },
  });

  const facets = useMemo(() => {
    return columns
      .map(
        (column) =>
          ({
            label: column.header?.toString() ?? '',
            value: column.id ?? column.accessorKey ?? '',
          } satisfies Facets),
      )
      .filter((column) => !hiddenFacets?.includes(column.value));
  }, [columns]);

  const handleActionSelect = (row: Row<TData>) => () => {
    onRowActionClick?.(row);
  };

  return (
    <div className="space-y-4">
      {!hideToolbar && (
        <DataTableToolbar
          table={table}
          disableFiltering={true}
          facets={facets}
          facetOptions={facetOptions}
          toolbarItems={toolbarItems}
          initialSelectedFacetValues={initialSelectedFacetValues}
          onSearch={onSearch}
          searchPlaceholder={searchPlaceholder}
        />
      )}
      <div className="overflow-x-auto rounded-md border">
        <Table className="border-0">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="border-0 text-inherit">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
                {rowActions?.length ? <TableHead className="border-0 text-inherit" /> : null}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {!loading && table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id + index}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClicked?.(row.getAllCells()[0].row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'border-0 text-left',
                        cell.column.columnDef.meta?.cellClassName ??
                          'max-w-sm truncate whitespace-nowrap',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                  {rowActions?.length && (
                    <TableCell className="border-0 text-left">
                      <DataTableRowActions
                        rowActions={rowActions}
                        onActionSelect={handleActionSelect(row)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : loading ? (
              new Array((pageSize ?? 20) / 2).fill(1).map((_, index) => (
                <TableRow key={'data-row-' + index}>
                  {table.getFlatHeaders().map((cells, index) => (
                    <TableCell key={cells.id} className="border-0 text-left">
                      <Skeleton className={`h-4 w-[${24 + index}px]`} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 border-0 text-center">
                  {emptyState ? emptyState : 'No data available'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-[24px]">
        <DataTablePagination
          pageSize={pageSize}
          table={table}
          showSkipToFirstPageButton={showSkipToFirstPageButton}
          showSkipToLastPageButton={showSkipToLastPageButton}
          hideTotalPageCount={hideTotalPageCount}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
        />
      </div>
    </div>
  );
}
